'use client'

/**
 * StickyStockNav — a scrollable row of section bookmarks for the (long) stock hub. It locks FLUSH under the
 * global fixed navbar as you scroll (owner 2026-07-17: it used to float ~64px below with an odd gap), so you
 * can jump straight to Chart / Key Stats / Why it's moving / News / Similar. The page scrolls inside <main>
 * (overflow-auto), NOT the window, whose scroll-viewport top already sits at the navbar bottom — so the bar
 * sticks at top-0 to sit flush. Clicking a chip computes the exact target scrollTop and scrolls the real
 * scroll container ONCE (scrollIntoView was landing partway in the nested container → the multi-click bug).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useEdgeFade } from '@/lib/hooks/useEdgeFade'

// The page scrolls inside <main> (overflow-auto), not the window, so find the real scroll ancestor.
function scrollParentOf(el: Element | null): HTMLElement | null {
  let p = el?.parentElement ?? null
  while (p && p !== document.documentElement) {
    const oy = getComputedStyle(p).overflowY
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 4) return p as HTMLElement
    p = p.parentElement
  }
  return null
}

export default function StickyStockNav({ sections, deferUntilMounted = false }: {
  sections: { id: string; label: string }[]
  /** Paint nothing until the mount filter runs. Only for a page whose sections mount AFTER its data loads
   *  (/alpaca), where the full list would paint and then vanish. */
  deferUntilMounted?: boolean
}) {
  // ⚠ STARTS WITH EVERY LISTED SECTION, so the bar is in the SERVER HTML (register
  // sticky-section-nav-inserted-after-mount-shifts-13-pages-1103). It used to start empty and return null, so the
  // 48px bar + mb-5 arrived after mount and pushed everything below it 68px: the largest layout shift on /stocks
  // and on 12 other pages (0.006-0.019 per load). The mount filter below now only drops chips for absent
  // sections, which moves chips INSIDE a bar whose height never changes.
  const [present, setPresent] = useState<{ id: string; label: string }[]>(() => (deferUntilMounted ? [] : sections))
  const [active, setActive] = useState<string>('')
  const [stuck, setStuck] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)

  // Keep only the sections that actually exist on this page (many are conditional on data).
  useEffect(() => {
    setPresent(sections.filter((s) => document.getElementById(s.id)))
  }, [sections])

  // The precise gap a jumped section must clear = the sticky bar's own height + a small breathing gap. The
  // bar sits FLUSH at the scroll container's top (right under the fixed navbar), so there's nothing else.
  const clearance = useCallback(() => (wrapRef.current?.offsetHeight ?? 48) + 10, [])

  const jumpRef = useRef(0)
  // One-click snap: compute the exact scrollTop within the REAL scroll container and scroll once. (Manual math
  // beats scrollIntoView, which lands partway in a nested overflow container and forced 2-3 clicks.) On heavy
  // pages (the Alpaca dashboard: portfolio chart + mini-charts + watchlist logos) the content GROWS while the
  // smooth scroll is running, so it lands on the stale target and stops partway. Re-snap to the freshly-computed
  // target as layout settles — retry for ~2s, instantly correcting any drift, bailing if another jump starts.
  const jump = useCallback((id: string) => {
    if (!document.getElementById(id)) return
    const scroller = scrollParentOf(document.getElementById(id))
    const myJump = ++jumpRef.current
    const targetTop = (): number | null => {
      const e = document.getElementById(id)
      if (!e) return null
      const offset = clearance()
      return scroller
        ? scroller.scrollTop + (e.getBoundingClientRect().top - scroller.getBoundingClientRect().top) - offset
        : window.scrollY + e.getBoundingClientRect().top - (64 + offset) // window fallback: + navbar
    }
    const scrollTo = (top: number, behavior: ScrollBehavior) => {
      const t = Math.max(0, top)
      if (scroller) scroller.scrollTo({ top: t, behavior })
      else window.scrollTo({ top: t, behavior })
    }
    const t0 = targetTop()
    if (t0 == null) return
    scrollTo(t0, 'smooth')
    let tries = 0
    const settle = () => {
      if (jumpRef.current !== myJump) return // a newer jump superseded this one
      const t = targetTop()
      if (t == null) return
      const cur = scroller ? scroller.scrollTop : window.scrollY
      if (Math.abs(cur - t) > 3) scrollTo(t, 'auto') // layout shifted → snap exactly to the fresh target
      if (++tries < 12) setTimeout(settle, 150)
    }
    setTimeout(settle, 300)
  }, [clearance])

  // Scrollspy: mark the section currently sitting just under the sticky bar as active. Observe against the
  // actual scroll container, with the top margin = the bar's clearance so "active" flips right at the bar.
  useEffect(() => {
    if (!present.length) return
    const root = scrollParentOf(document.getElementById(present[0].id))
    const top = clearance()
    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (vis[0]) setActive(vis[0].target.id)
      },
      { root, rootMargin: `-${top}px 0px -65% 0px`, threshold: 0 },
    )
    present.forEach((s) => { const el = document.getElementById(s.id); if (el) io.observe(el) })
    return () => io.disconnect()
  }, [present, clearance])

  // "Stuck" state → a subtle shadow so the bar reads as a toolbar once it locks under the navbar.
  useEffect(() => {
    if (!present.length) return
    const scroller = scrollParentOf(wrapRef.current)
    const target: HTMLElement | Window = scroller ?? window
    const onScroll = () => {
      const wrapTop = wrapRef.current?.getBoundingClientRect().top ?? 9999
      const refTop = scroller ? scroller.getBoundingClientRect().top : 0
      setStuck(wrapTop <= refTop + 1)
    }
    target.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => target.removeEventListener('scroll', onScroll)
  }, [present])

  // Keep the active chip scrolled into view within the horizontal bar (its own scroll, not the page).
  useEffect(() => {
    if (!active || !barRef.current) return
    const chip = barRef.current.querySelector<HTMLElement>(`[data-sec="${active}"]`)
    chip?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [active])

  // Edge-fade affordance: the bar hides its scrollbar for a clean look, so without this there is NO hint
  // that Reddit / Insiders / Dilution / News / Similar sit past the visible edge. useEdgeFade masks the
  // overflowing side(s) into the bar's own background — recomputed on the bar's scroll (incl. the
  // active-chip auto-scroll above) and re-measured when the section list settles.
  const mask = useEdgeFade(barRef, [present])

  if (present.length < 2) return null

  return (
    <div
      ref={wrapRef}
      className={`sticky top-0 z-20 -mx-4 mb-5 border-b px-3 backdrop-blur-md transition-[box-shadow,background-color,border-color] duration-200 ${
        stuck ? 'border-border bg-background/95 shadow-lg dark:shadow-black/40' : 'border-border/60 bg-background/80'
      }`}
    >
      <div ref={barRef} style={{ maskImage: mask, WebkitMaskImage: mask }} className="flex gap-1.5 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {present.map((s) => {
          const on = active === s.id
          return (
            <button
              key={s.id}
              data-sec={s.id}
              onClick={() => jump(s.id)}
              aria-current={on ? 'true' : undefined}
              className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-all ${
                on
                  ? 'bg-amber-500 text-black shadow-sm shadow-amber-500/30'
                  : 'text-muted-foreground hover:bg-accent dark:hover:bg-zinc-800/70 hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
